import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LogOut, Users, Package, MapPin, Settings, FileText, ClipboardList,
  LayoutDashboard, Thermometer, Shield, Wrench, Upload, Image,
  ChevronDown, ChevronRight, Layers, Clock, UserCog, Sparkles
} from 'lucide-react';

const getNavSections = (role) => {
  const sections = [
    {
      label: 'Principal',
      items: [
        { path: '/', label: 'Tableau de bord', icon: LayoutDashboard },
        { path: '/plans', label: 'Plans HACCP', icon: ClipboardList },
        { path: '/plan-builder', label: 'Creer un plan', icon: FileText },
        { path: '/plan-ai', label: 'Plan avec IA', icon: Sparkles },
        { path: '/import', label: 'Import Excel', icon: Upload },
      ],
    },
    {
      label: 'Donnees metier',
      items: [
        { path: '/clients', label: 'Clients', icon: Users },
        { path: '/products', label: 'Produits', icon: Package },
        { path: '/methods', label: 'Methodes', icon: Layers },
        { path: '/zones', label: 'Zones', icon: MapPin },
        { path: '/surface-items', label: 'Surfaces', icon: Settings },
      ],
    },
    {
      label: 'Parametrage',
      items: [
        { path: '/frequencies', label: 'Frequences', icon: Clock },
        { path: '/icons', label: 'Icones', icon: Image },
        { path: '/equipment', label: 'Equipements', icon: Wrench },
        { path: '/temperatures', label: 'Temperatures', icon: Thermometer },
        { path: '/controls', label: 'Controles', icon: Shield },
        { path: '/templates', label: 'Modeles', icon: FileText },
      ],
    },
  ];

  if (role === 'admin') {
    sections.push({
      label: 'Administration',
      items: [
        { path: '/users', label: 'Utilisateurs', icon: UserCog },
      ],
    });
  }

  return sections;
};

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [collapsedSections, setCollapsedSections] = useState({});

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const toggleSection = (label) => {
    setCollapsedSections(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const navSections = getNavSections(user?.role);

  return (
    <div className="flex min-h-screen" style={{ backgroundColor: 'var(--ph-sand)' }}>
      {/* Sidebar */}
      <aside
        className="fixed left-0 top-0 bottom-0 flex flex-col sidebar-scroll overflow-y-auto"
        style={{
          width: '260px',
          background: 'linear-gradient(180deg, #0E2D43 0%, #0B2236 100%)',
          zIndex: 40,
        }}
      >
        {/* Logo */}
        <div className="px-6 pt-6 pb-5">
          <Link to="/" className="block">
            <span
              className="text-xl font-bold tracking-tight"
              style={{ color: '#fff', fontFamily: 'var(--font-display)' }}
            >
              PREST'HYG
            </span>
            <span className="block text-xs mt-0.5 font-medium" style={{ color: 'rgba(255,255,255,0.4)' }}>
              Solutions HACCP Premium
            </span>
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-3 pb-4">
          {navSections.map((section) => {
            const isCollapsed = collapsedSections[section.label];
            return (
              <div key={section.label} className="mb-2">
                <button
                  onClick={() => toggleSection(section.label)}
                  className="w-full flex items-center justify-between px-3 py-2 text-xs font-semibold uppercase tracking-wider"
                  style={{ color: 'rgba(255,255,255,0.35)' }}
                >
                  <span>{section.label}</span>
                  {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                </button>

                {!isCollapsed && (
                  <div className="space-y-0.5">
                    {section.items.map((item) => {
                      const Icon = item.icon;
                      const isActive = location.pathname === item.path;
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          className="flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all"
                          style={{
                            borderRadius: 'var(--ph-radius-sm)',
                            color: isActive ? '#fff' : 'rgba(255,255,255,0.65)',
                            backgroundColor: isActive ? 'rgba(22,119,134,0.35)' : 'transparent',
                          }}
                        >
                          <Icon size={18} style={{ opacity: isActive ? 1 : 0.6 }} />
                          <span>{item.label}</span>
                          {isActive && (
                            <span
                              className="ml-auto w-1.5 h-1.5 rounded-full"
                              style={{ backgroundColor: 'var(--ph-teal)' }}
                            />
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        {/* User footer */}
        <div className="px-4 py-4 border-t" style={{ borderColor: 'rgba(255,255,255,0.08)' }}>
          <div className="flex items-center gap-3 mb-3">
            <div
              className="w-9 h-9 flex items-center justify-center text-sm font-bold"
              style={{
                borderRadius: 'var(--ph-radius-sm)',
                backgroundColor: 'var(--ph-teal)',
                color: '#fff',
              }}
            >
              {(user?.name || 'U')[0].toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate" style={{ color: '#fff' }}>{user?.name}</p>
              <p className="text-xs truncate" style={{ color: 'rgba(255,255,255,0.4)' }}>{user?.role}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full flex items-center justify-center gap-2 px-3 py-2 text-xs font-medium transition-all"
            style={{
              borderRadius: 'var(--ph-radius-sm)',
              color: 'rgba(255,255,255,0.5)',
              border: '1px solid rgba(255,255,255,0.1)',
            }}
          >
            <LogOut size={14} />
            <span>Deconnexion</span>
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1" style={{ marginLeft: '260px' }}>
        {/* Topbar */}
        <header
          className="sticky top-0 flex items-center justify-between px-8 py-4 border-b"
          style={{
            backgroundColor: 'rgba(247,245,241,0.85)',
            backdropFilter: 'blur(12px)',
            borderColor: 'var(--ph-slate200)',
            zIndex: 30,
          }}
        >
          <div>
            <h2 className="text-base font-semibold" style={{ color: 'var(--ph-navy)', fontFamily: 'var(--font-body)' }}>
              {navSections.flatMap(s => s.items).find(i => i.path === location.pathname)?.label || 'PREST\'HYG'}
            </h2>
          </div>
          <div className="flex items-center gap-4">
          </div>
        </header>

        {/* Page content */}
        <main className="px-8 py-8">{children}</main>
      </div>
    </div>
  );
};

export default Layout;
